import { FC } from 'react';
import { Box, Card, formatDate, withStyles } from 'vopay';
import styles from '../VerificationCard/VerificationCard.module.scss';
import { Button } from '@/components/UI';
import { useLockedFeatures } from '@/providers/LockedFeatures.provider';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useToast } from '@/providers/Toast.provider';
import { useTranslation } from 'react-i18next';
import { useDialog } from '@/providers/Dialog.provider';
import UpgradePlanCardDialog from '@/components/Customer/Tabs/PaymentMethods/UpgradePlanCardDialog/UpgradePlanCardDialog';
import EnhancedVerificationCardDialog from '@/components/Customer/Tabs/PaymentMethods/EnhancedVerificationCardDialog/EnhancedVerificationCardDialog';
import useClientAccountPaymentDetails from '@/hooks/usePaymentMethodDetails';
import type { CardDetails } from '../PaymentMethodDetails';

const cn = withStyles(styles);

type CardVerificationCardProps = {
  card: CardDetails;
  accountID: string;
  email: string;
  onVerify: () => void;
};

const CardVerificationCard: FC<CardVerificationCardProps> = ({ card, accountID, email, onVerify }) => {
  const { CardBasicVerificationEnabled, CardEnhancedVerificationEnabled } = useLockedFeatures();
  const { t } = useTranslation('translations', { keyPrefix: 'Customers.PaymentMethodsTab.Details' });
  const alertDialog = useAlertDialog();
  const toast = useToast();
  const dialog = useDialog();

  const { verifyCardAccount } = useClientAccountPaymentDetails({
    accountID,
    email,
  });

  const handleCardSendVerification = async () => {
    const closeType = await alertDialog.open(
      t('Verification.Dialog.sendVerificationTitle', { defaultValue: 'Send Verification Request?' }),
      t('CardVerification.Dialog.sendVerificationMessage', {
        defaultValue: 'We will check if the card holder name and billing address matches the card processor record.',
      }),
      {
        confirmLabel: t('Verification.Dialog.confirm', { defaultValue: 'Confirm' }),
        bottom: t('Verification.Dialog.sendVerificationBottom', {
          defaultValue: 'This action does not notify the client',
        }),
        abortLabel: t('Verification.Dialog.cancel', { defaultValue: 'Cancel' }),
      },
    );
    if (closeType === 'confirm') {
      try {
        const res = await verifyCardAccount(card.Token, 'basic');

        if (res.Success) {
          toast.success(
            t('CardVerification.Toast.verificationRequestSent', {
              defaultValue: `Card Verification request sent`,
            }),
          );
          await onVerify();
        }
      } catch (err) {
        if (err instanceof Error) alertDialog.error(t('error', { defaultValue: 'Error' }), err.message);
      }
    }
  };

  const handleEnhancedCardSendVerification = async () => {
    const closeType = await alertDialog.open(
      t('Verification.Dialog.sendVerificationTitle', { defaultValue: 'Send Verification Request?' }),

      <Box flx="col" ai="fs" p="sm">
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            textAlign: 'left',
            gap: '24px',
            color: 'rgba(45, 45, 45, 0.75)',
          }}
        >
          <p>
            {t('CardVerification.Dialog.sendVerificationMessage', {
              defaultValue:
                'We will check if the card holder name and billing address matches the card processor record.',
            })}
          </p>
          <div>
            <p>
              {t('Verification.Dialog.enhancedVerificationMessage', {
                defaultValue: 'We will also retrieve the following details:',
              })}
            </p>
            <ul style={{ listStylePosition: 'outside', paddingLeft: '20px' }}>
              <li>
                {t('CardVerification.Dialog.enhancedVerificationDetails.cardHolderName', {
                  defaultValue: 'Card Holder Name',
                })}
              </li>
              <li>
                {t('CardVerification.Dialog.enhancedVerificationDetails.cardType', { defaultValue: 'Card Type' })}
              </li>
              <li>
                {t('CardVerification.Dialog.enhancedVerificationDetails.cardBrand', { defaultValue: 'Card Brand' })}
              </li>
              <li>
                {t('CardVerification.Dialog.enhancedVerificationDetails.sendReceiveEligibility', {
                  defaultValue: 'Send & Receive Funds Eligibility',
                })}
              </li>
              <li>
                {t('CardVerification.Dialog.enhancedVerificationDetails.estimatedFundAvailability', {
                  defaultValue: 'Estimated Fund Availability',
                })}
              </li>
            </ul>
          </div>
        </div>
      </Box>,
      {
        confirmLabel: t('Verification.Dialog.confirm', { defaultValue: 'Confirm' }),
        bottom: t('Verification.Dialog.sendVerificationBottom', {
          defaultValue: 'This action does not notify the client',
        }),
        abortLabel: t('Verification.Dialog.cancel', { defaultValue: 'Cancel' }),
      },
    );
    if (closeType === 'confirm') {
      try {
        const res = await verifyCardAccount(card.Token, 'enhanced');

        if (res.Success) {
          toast.success(
            t('CardVerification.Toast.verificationRequestSent', {
              defaultValue: `Card Verification request sent`,
            }),
          );
          await onVerify();
        }
      } catch (err) {
        if (err instanceof Error) alertDialog.error(t('error', { defaultValue: 'Error' }), err.message);

        console.error(err);
      }
    }
  };

  function getEnabledStatus(value?: boolean): string {
    if (value === true) return t('Verification.enabled', { defaultValue: 'Enabled' });
    if (value === false) return t('Verification.disabled', { defaultValue: 'Disabled' });
    return '-';
  }

  const rows = [
    {
      key: t('Verification.status', { defaultValue: 'Verification Status' }),
      val: (
        <>
          <p className={cn('verificationLabel', card.verificationStatus)}>
            {card.verificationStatus === 'verified'
              ? t('Verification.verified', { defaultValue: 'Verified' })
              : card.verificationStatus === 'unverified'
                ? t('Verification.notVerified', { defaultValue: 'Not Verified' })
                : t('Verification.failed', { defaultValue: 'Verification Failed' })}
          </p>
        </>
      ),
    },
    {
      key: t('CardVerification.cardHolderName', { defaultValue: 'Card Holder Name' }),
      val: card.CardHolderName,
    },
    { key: t('CardVerification.cardType', { defaultValue: 'Card Type' }), val: card.Type },
    {
      key: t('CardVerification.cardBrand', { defaultValue: 'Card brand' }),
      val: card.Brand,
    },
    {
      key: t('CardVerification.canSendFunds', { defaultValue: 'Can Send Funds' }),
      val: getEnabledStatus(card.SendingEligibility),
    },
    {
      key: t('CardVerification.canReceiveFunds', { defaultValue: 'Can Receive Funds' }),
      val: getEnabledStatus(card.ReceivingEligibility),
    },
    {
      key: t('CardVerification.estimatedFundAvailability', { defaultValue: 'Estimated Fund Availability' }),
      val: card.FundsAvailability,
    },
  ].map((row, index) => ({ key: row.key, val: row.val || index === 0 ? row.val : '-' }));

  const cardVerificationLockedStyle = !CardBasicVerificationEnabled
    ? 'fullyLocked'
    : !card.verificationSentOn && !CardEnhancedVerificationEnabled
      ? 'partiallyLocked'
      : undefined;

  async function handleUnlockVerificationRequestClick() {
    await dialog.open<boolean>((args) => <UpgradePlanCardDialog {...args} />);
  }

  async function handleUnlockEnhancedVerificationRequestClick() {
    await dialog.open<boolean>((args) => <EnhancedVerificationCardDialog {...args} />);
  }

  return (
    <div className={cn('verificationCard', CardEnhancedVerificationEnabled && 'enhanced', cardVerificationLockedStyle)}>
      <Card style={{ padding: '16px' }} hoverEffect={false}>
        <span className={cn('cardContent')}>
          <div className={cn('header')}>
            <h5 className={cn('title')}>{t('Verification.title', { defaultValue: 'Verification Details' })}</h5>
            {card.verificationSentOn && (
              <p className={cn('requestSentOn')}>
                {t('Verification.lastRequestSentOn', { defaultValue: 'Last request sent on' })}{' '}
                {formatDate(card.verificationSentOn, 'yyyy-MM-dd HH:mm:ss')}
              </p>
            )}
          </div>
          <span>
            {rows.map(({ key, val }) => (
              <div key={key} className={cn('row')}>
                <div>{key}</div>
                <div>{val}</div>
              </div>
            ))}
          </span>
          <div className={cn('footer')}>
            {!CardBasicVerificationEnabled && (
              <div className={cn('buttonContainer')}>
                <Button
                  iconRightProps={{ icon: 'lock', fillAmount: 1 }}
                  size="small"
                  colorScheme="primary"
                  onClick={handleUnlockVerificationRequestClick}
                >
                  {t('Verification.unlockVerificationRequest', { defaultValue: 'Unlock Verification Request' })}
                </Button>
              </div>
            )}

            {CardBasicVerificationEnabled && (
              <div className={cn('buttonContainer')}>
                <Button size="small" onClick={() => handleCardSendVerification()}>
                  {t('Verification.basicVerificationRequest', { defaultValue: 'Basic Verification Request' })}
                </Button>

                {CardEnhancedVerificationEnabled ? (
                  <Button
                    colorScheme="feature"
                    size="small"
                    iconRightProps={{ icon: 'auto_awesome', color: '#fbfbfb', fillAmount: 1 }}
                    onClick={handleEnhancedCardSendVerification}
                  >
                    {t('Verification.enhancedVerificationRequest', { defaultValue: 'Enhanced Verification Request' })}
                  </Button>
                ) : (
                  <Button
                    colorScheme="primary"
                    iconRightProps={{ icon: 'lock', fillAmount: 1 }}
                    size="small"
                    onClick={handleUnlockEnhancedVerificationRequestClick}
                  >
                    {t('Verification.unlockEnhancedVerification', { defaultValue: 'Unlock Enhanced Verification' })}
                  </Button>
                )}
              </div>
            )}
          </div>
        </span>
      </Card>
    </div>
  );
};

export default CardVerificationCard;
